import {
  Box,
  Button,
  IconButton,
  Modal,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useEffect } from "react";
import { LoadingButton } from "@mui/lab";
import { useRecoilState } from "recoil";
import { notificationsState } from "src/store/notifications";
import { useTranslation } from "react-i18next";
import { useUpdateTreeCategoryMutation } from "src/api/tree/tree";

const schema = yup.object().shape({
  name: yup.string().required("Required"),
  hasBalance: yup.boolean().required("Required"),
  balance: yup.number().when("hasBalance", {
    is: true,
    then: (schema) => schema.required("Required"),
    otherwise: (schema) => schema.notRequired().nullable(),
  }),
});

interface FormInputs extends yup.InferType<typeof schema> {}

interface IModel {
  open: boolean;
  handleClose: () => void;
  dataToEdit?: any;
  refetch: Function;
}

const EditCategory = (props: IModel) => {
  const { open, handleClose, dataToEdit, refetch } = props;
  const [notifications, setNotifications] = useRecoilState(notificationsState);
  const { control, handleSubmit, formState, reset, setValue } =
    useForm<FormInputs>({
      mode: "onBlur",
      resolver: yupResolver(schema),
    });
  const { t } = useTranslation("settings");
  const { t: generalT } = useTranslation("general");
  // APIS
  const {
    mutate: mutateUpdate,
    isPending: isPendingUpdate,
    status: statusUpdate,
    error: errorUpdate,
  } = useUpdateTreeCategoryMutation();

  const onSubmit: SubmitHandler<FormInputs> = async (data) => {
    mutateUpdate({
      id: dataToEdit.id,
      name: data.name,
      ...(data.hasBalance ? { start_balance: data.balance ?? 0 } : {}),
    });
  };

  useEffect(() => {
    if (dataToEdit) {
      reset({
        name: dataToEdit.name,
        hasBalance: !!dataToEdit?.has_balance,
        ...(dataToEdit?.has_balance ? { balance: dataToEdit.balance } : {}),
      });
    }
  }, [dataToEdit]);

  //Update user success and error handling
  useEffect(() => {
    if (statusUpdate === "success") {
      handleClose();
      refetch();
      setNotifications([
        ...notifications,
        {
          type: "success",
          message: generalT("updatedSuccessfully"),
        },
      ]);
    } else if (statusUpdate === "error") {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message:
            errorUpdate?.data.errors[0].value || generalT("somethingWrong"),
        },
      ]);
    }
  }, [statusUpdate]);

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Stack
        spacing={3}
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 537,
          bgcolor: "#FFF",
          border: "1px solid #D0D5DD",
          borderRadius: 1,
          boxShadow: 24,
          p: 3,
        }}
      >
        <Box
          display={"flex"}
          justifyContent={"space-between"}
          alignItems={"center"}
          p={0}
        >
          <Typography color={"#101828"} fontSize={"20px"}>
            {generalT("edit")}
          </Typography>
          <IconButton onClick={handleClose} sx={{ p: 0 }}>
            <CloseIcon />
          </IconButton>
        </Box>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack spacing={2}>
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <TextField
                  label={t("paymentName")}
                  fullWidth
                  size="small"
                  {...field}
                  error={!!formState.errors.name}
                  helperText={formState.errors.name?.message}
                />
              )}
            />
            {dataToEdit?.has_balance && (
              <Controller
                name="balance"
                control={control}
                render={({ field }) => (
                  <TextField
                    type="number"
                    label={t("startBalance")}
                    fullWidth
                    size="small"
                    {...field}
                    error={!!formState.errors.balance}
                    helperText={formState.errors.balance?.message}
                  />
                )}
              />
            )}
          </Stack>
          <Stack spacing={2} direction={"row"} mt={3}>
            <Button
              variant="outlined"
              color="tertiary"
              fullWidth
              onClick={handleClose}
            >
              {generalT("cancel")}
            </Button>
            <LoadingButton
              type="submit"
              loading={isPendingUpdate}
              variant="contained"
              fullWidth
            >
              {generalT("save")}
            </LoadingButton>
          </Stack>
        </form>
      </Stack>
    </Modal>
  );
};

export default EditCategory;
